import { requisitar } from "./http";

export type UnidadeDeMedida = "KG" | "G" | "L" | "ML" | "UN";

export type OrigemDoLote = "PROPRIA" | "TERCEIRO";

export type SituacaoDoLote =
  | "QUARENTENA"
  | "APROVADO"
  | "REPROVADO"
  | "BLOQUEADO"
  | "CONSUMIDO";

/**
 * Por qual coluna a lista de lotes se ordena.
 *
 * ⚠️ **É uma lista fechada, e não um nome de campo.** `order by` do SQL não
 * aceita parâmetro ligado: quem monta a cláusula com o que o navegador mandou
 * concatena texto do cliente dentro do SQL. O servidor converte este valor num
 * enum antes de qualquer coisa, e nome desconhecido é **recusado**, não
 * ignorado — lista que se ordena por outra coisa em silêncio é lista que a
 * pessoa confere achando que está vendo o mais recente.
 *
 * `QUANTIDADE` só existe aqui porque a ordenação é do **servidor**: a
 * quantidade chega como texto (`"120.000000"`), e ordenar texto no navegador
 * poria 120 antes de 25.
 */
export type ColunaDeLotes =
  | "RECEBIMENTO"
  | "QUANTIDADE"
  | "VALIDADE"
  | "CODIGO"
  | "SITUACAO";

export type SentidoDeOrdenacao = "ASC" | "DESC";

/**
 * Busca, filtros e ordenação da lista de lotes — **tudo resolvido no servidor**.
 *
 * ⚠️ **A empresa não está aqui de propósito.** Ela é o primeiro argumento de
 * `producao.lotes`, é obrigatória, e ausência dela é **recusa** — nunca "todas"
 * (ADR-0005 §1). Os campos deste objeto são filtros: ausentes, não filtram
 * nada. Guardar os dois na mesma estrutura convidaria alguém a tratar empresa
 * ausente como opcional, que é como o material de uma empresa aparece na tela
 * da outra.
 */
export interface RecorteDeLotes {
  /**
   * Texto livre. **Alcança**: código do lote · código **e nome** da
   * matéria-prima · lote do fornecedor · nota fiscal — todos por **trecho** —,
   * e o **token do volume** (32 hexadecimais) e o **código do recipiente**
   * (`R-0047`) por **igualdade**.
   *
   * 🔑 Os dois últimos são por igualdade de propósito. Trecho de token não
   * significa nada — o valor é sorteado, não há prefixo nem faixa — e seria um
   * oráculo: `%ab%` devolveria os tambores cujo token contém aquele par, sobre
   * um identificador que o ADR-0006 fez opaco justamente para não ser
   * enumerável. Colar o token com os hífens de leitura
   * (`23d9-6d7d-…`) funciona: o servidor os descarta.
   *
   * 🔴 **Não alcança o nome do fornecedor.** Ele mora em outro módulo, e
   * `producao` não junta schema com ele — é pendência declarada, não
   * esquecimento. Também não alcança a observação do lote (texto livre sem
   * regra de preenchimento).
   */
  termo?: string;
  /**
   * As situações a mostrar. **Vazio ou ausente é TODAS**, nunca nenhuma.
   *
   * Hoje só `QUARENTENA` é alcançável — não existe `aprovar()`, e a ausência é
   * o controle. As demais existem no filtro para que a entrada delas seja
   * acréscimo, não reestruturação.
   */
  situacao?: readonly SituacaoDoLote[];
  /** Início do período de **recebimento**, `AAAA-MM-DD`, **inclusive**. */
  recebidoDe?: string;
  /** Fim do período de **recebimento**, `AAAA-MM-DD`, **inclusive**. */
  recebidoAte?: string;
  /** Ausente é `RECEBIMENTO` + `DESC` — o mais recente em cima, como sempre foi. */
  ordenarPor?: ColunaDeLotes;
  /** Ausente com `ordenarPor` presente vale `ASC`. */
  sentido?: SentidoDeOrdenacao;
}

export interface Empresa {
  id: string;
  slug: string;
  nomeFantasia: string;
  apelido: string;
  razaoSocial: string;
  /** Já vem com máscara: o banco guarda só dígitos. */
  cnpj: string;
  papel: "PRODUTORA" | "DISTRIBUIDORA";
}

/**
 * O que o material **é** — a primeira parte do código do lote.
 *
 * `MP-COP-318425`: **MP** matéria-prima · **EX** extrato · **MT** manteiga ·
 * **SB** sobra. A sigla sai vazada num quadrado preto na etiqueta, e é o que se
 * reconhece a três metros de prateleira — o caso real é óleo, extrato e
 * manteiga da mesma copaíba lado a lado.
 */
export type TipoDeMaterial = "MP" | "EX" | "MT" | "SB";

export interface MateriaPrima {
  id: string;
  codigo: string;
  nome: string;
  tipo: TipoDeMaterial;
  /** Três letras da espécie vegetal — `COP`, `ALG`, `VET`. */
  especie: string;
  unidade: UnidadeDeMedida;
}

export interface Fornecedor {
  id: string;
  nome: string;
  cnpj: string | null;
}

export interface Lote {
  id: string;
  codigo: string;
  /** Obrigatório e imutável — ADR-0005. */
  empresaId: string;
  /**
   * O apelido da empresa dona do lote, resolvido no **servidor**.
   *
   * Não é junção feita aqui: na visão consolidada, uma linha cujo
   * identificador de empresa não casasse com a lista carregada pelo cliente
   * sairia com o selo **vazio** — material sem CNPJ na tela, que é exatamente
   * o que o selo existe para impedir.
   *
   * `null` é o estado nomeado de "sem dono". Inalcançável hoje (o servidor
   * filtra os lotes pelo mesmo conjunto de onde tira o nome), e desenhado como
   * defeito quando acontecer.
   */
  empresa: string | null;
  materiaPrima: string;
  codigoMateriaPrima: string;
  /**
   * A sigla do selo — lida **de dentro do código do lote**, não do cadastro.
   *
   * `null` nos lotes anteriores ao código composto (`MP-0002`): eles não têm
   * família registrada, e a etiqueta deles não desenha selo. Nulo é a ausência
   * nomeada, não dado que se perdeu — o cadastro pode ter sido corrigido depois,
   * e o que está colado no tambor não muda junto.
   */
  tipo: string | null;
  /** O tipo por extenso, resolvido no servidor — `MATÉRIA-PRIMA`. */
  palavra: string | null;
  especie: string | null;
  origem: OrigemDoLote;
  /** "Fazenda própria" quando a origem é do grupo. */
  fornecedor: string;
  loteDoFornecedor: string | null;
  notaFiscal: string | null;
  quantidade: string;
  /** Símbolo pronto para exibir — `kg`, `mL`. */
  unidade: string;
  /** Em quantos recipientes o lote chegou. Cada um recebe uma etiqueta. */
  volumes: number;
  /**
   * O total **dividido** pela contagem — a **média** do lote.
   *
   * ⚠️ **A etiqueta não imprime mais este número.** Desde que o volume virou
   * registro próprio, cada etiqueta traz a quantidade **gravada** do recipiente
   * dela (`VolumeDaEtiqueta.quantidade`). Este campo continua existindo para a
   * lista e para a ficha do lote, onde a pergunta é mesmo sobre o lote — e ali
   * a tela diz "média" com todas as letras.
   *
   * Vem como texto pelo mesmo motivo que `quantidade`: número em JSON vira
   * `double` em qualquer cliente JavaScript, e a precisão da pesagem some antes
   * de chegar à tela.
   */
  quantidadePorVolume: string;
  recebidoEm: string;
  validade: string;
  situacao: SituacaoDoLote;
  /** Calculado no SERVIDOR: comparar data no navegador depende do relógio de quem abre. */
  vencido: boolean;
}

/**
 * Um recipiente do lote — **uma etiqueta, um QR**.
 *
 * O pedido do PO que criou isto: *"cada QR Code vai ser referente a 1 bombona,
 * e o código deveria ser `/qr/<token daquela etiqueta específica>`"*. Vinte
 * bombonas do mesmo lote são vinte destes, com vinte tokens diferentes.
 */
export interface VolumeDaEtiqueta {
  /** 1..N dentro do lote — o `7` de `VOLUME 7/20`. */
  numero: number;
  /**
   * O peso **deste** recipiente, **gravado** no servidor.
   *
   * Hoje ele nasce da divisão do total, como antes. O que mudou é a natureza:
   * deixou de ser conta feita na hora de exibir e passou a ser dado do
   * recipiente — a balança da conferência o corrige sem que nada aqui mude.
   *
   * Texto, como toda quantidade: número em JSON vira `double`.
   */
  quantidade: string;
  /**
   * O que vai dentro do QR desta etiqueta — `/qr/<token>`, ADR-0006.
   *
   * **32 hexadecimais** — um UUID v4 sem hífen, sorteado à parte da chave
   * primária (`V14`, decisão do PO). Ele chega **cru**, e quem imprime agrupa
   * de quatro em quatro (`linhasDoToken`, em `@plantus/ui`).
   *
   * Eram **oito** caracteres de um alfabeto de trinta sem `0`/`O`, `1`/`I`/`L`
   * e `U`. O alfabeto saiu de cena, e o **princípio dele segue respeitado por
   * outro caminho: hexadecimal não tem par confundível** — `0` e `1` estão no
   * alfabeto, `O`, `I` e `L` não estão, então os pares nunca coexistem e não há
   * o que desambiguar no papel. Quem digitar a letra no lugar do dígito é
   * recusado **alto**, na página de socorro, em vez de ser levado em silêncio
   * ao tambor errado.
   *
   * ⚠️ E `curto` deixou de valer: 32 hexadecimais **não se redigitam** de um
   * adesivo sujo de óleo. O token impresso virou **conferência**; o caminho
   * manual é o **código do lote** (ADR-0006; feature 1.2.2 R13).
   */
  token: string;
  /**
   * Quantas etiquetas **deste** recipiente já saíram.
   *
   * `0` significa que a próxima é primeira via. Qualquer outro valor significa
   * que a próxima é **reimpressão** — e reimpressão exige motivo, recusada pelo
   * servidor e pelo banco se vier sem.
   *
   * A tela precisa saber disso **antes** do clique: descobrir que falta o
   * motivo depois de a caixa de impressão abrir é descobrir tarde.
   */
  viasEmitidas: number;
  /**
   * O código da plaqueta — `R-0047` — ou **`null`** quando esta ocupação ainda
   * não sabe em qual tambor está.
   *
   * `null` é **estado nomeado**, não dado que se perdeu: nenhuma das ocupações
   * anteriores ao ADR-0012 foi retrocarregada, porque não há plaqueta a ler e
   * inventar o vínculo seria o defeito que a cópia de rótulo existe para
   * impedir. Quem exibe escreve *"recipiente não identificado"* — espaço em
   * branco se lê como "o dado existia e alguém esqueceu".
   */
  recipiente: string | null;
}

/** O que saiu de uma folha — uma linha por adesivo, com a via de cada um. */
export interface EtiquetaEmitida {
  /** O número do recipiente — o `7` de `VOLUME 7/20`. */
  volume: number;
  /** 1 na primeira; 2, 3, … nas reimpressões. A via é do **volume**. */
  via: number;
  reimpressao: boolean;
}

/** O volume que o QR abriu, com o lote inteiro junto. */
export interface VolumeNaTela {
  numero: number;
  /** Quantos recipientes o lote tem — o `20` de `VOLUME 7/20`. */
  total: number;
  quantidade: string;
  token: string;
  /** O código da plaqueta, ou `null` — a ficha **diz** que não sabe. */
  recipiente: string | null;
  lote: Lote;
}

/** O que chegou e aguarda decisão da qualidade. **Não** é saldo de estoque. */
export interface QuarentenaPorMateriaPrima {
  materiaPrimaId: string;
  codigo: string;
  nome: string;
  quantidade: string;
  unidade: string;
  lotes: number;
  /** Passaram da validade **antes** de serem analisados. */
  vencidos: number;
  validadeMaisProxima: string;
}

export interface PedidoDeRecebimento {
  empresa: string;
  materiaPrima: string;
  origem: OrigemDoLote;
  /** Nulo quando a origem é a fazenda do grupo. */
  fornecedor: string | null;
  loteDoFornecedor: string | null;
  notaFiscal: string | null;
  quantidade: string;
  /** Quantos recipientes. Ausente vale 1 — o caso comum é um tambor. */
  volumes: number;
  recebidoEm: string;
  validade: string;
  observacao: string | null;
  /**
   * Cada volume ganha a **identidade** de um recipiente novo (`R-0047`) na
   * mesma transação.
   *
   * ⚠️ **Identidade, não dossiê** (ADR-0012 item 6): tipo, construção,
   * capacidade, massa máxima e tara não são pedidos no recebimento. Dado de
   * ativo digitado com o caminhão esperando é dado que ninguém confia depois —
   * e é o dado que o portão de reuso vai ler para decidir.
   *
   * Falso quando o material vai para tambores que **já têm plaqueta**: aí o
   * vínculo é feito lendo o código que está no tambor.
   */
  identificarRecipientes: boolean;
}

export const empresas = {
  listar: () => requisitar<Empresa[]>("/empresas"),
};

export const producao = {
  /**
   * O que chegou e aguarda decisão. **Não é saldo** — não há saída no sistema
   * ainda, e chamar de saldo seria correto hoje e errado no dia seguinte ao
   * primeiro consumo.
   */
  quarentena: (empresa: string) =>
    requisitar<QuarentenaPorMateriaPrima[]>(`/producao/quarentena?empresa=${empresa}`),

  /**
   * Os lotes de **uma ou mais** empresas, numa requisição só.
   *
   * <p>`?empresa=A&empresa=B` é a visão consolidada. Não são duas chamadas
   * juntadas aqui, e a diferença não é de desempenho:
   *
   * - **a ordenação é do banco.** Juntar duas listas prontas obriga o cliente a
   *   reordenar, e duas páginas ordenadas não se juntam numa página ordenada;
   * - **falha parcial viraria lista plausível.** Se uma das chamadas falhasse,
   *   o consolidado apareceria completo e sem o material de um CNPJ — e dado
   *   plausível é pior que dado visivelmente errado;
   * - **o recorte por permissão é do servidor** (ADR-0003 §5). Com o conjunto
   *   numa requisição, existe um lugar onde interseccioná-lo com as empresas
   *   que o usuário pode ver. Com N chamadas, quem escolhe o conjunto é o
   *   cliente.
   *
   * <p>Lista vazia não vira "todas": a chamada nem sai. O servidor também
   * recusa a requisição sem o parâmetro.
   */
  lotes: (empresas: readonly string[], termo?: string, recorte?: RecorteDeLotes) => {
    if (empresas.length === 0) {
      return Promise.resolve<Lote[]>([]);
    }
    // `termo` continua como segundo argumento posicional porque a tela já o
    // chama assim. Quem passa o recorte completo pode repetir o termo dentro
    // dele; o de dentro ganha, para que `lotes(ids, "", recorte)` não apague o
    // termo do recorte sem avisar.
    const pedido: RecorteDeLotes = { termo, ...recorte };
    const partes = empresas.map((e) => `empresa=${encodeURIComponent(e)}`);

    if (pedido.termo !== undefined && pedido.termo !== "") {
      partes.push(`termo=${encodeURIComponent(pedido.termo)}`);
    }
    // Uma repetição por situação, como as empresas: `situacao=A&situacao=B`.
    // Lista vazia NÃO vira `situacao=` — parâmetro vazio é valor desconhecido,
    // e o servidor recusa valor desconhecido em vez de ignorá-lo.
    for (const situacao of pedido.situacao ?? []) {
      partes.push(`situacao=${situacao}`);
    }
    if (pedido.recebidoDe !== undefined && pedido.recebidoDe !== "") {
      partes.push(`recebidoDe=${pedido.recebidoDe}`);
    }
    if (pedido.recebidoAte !== undefined && pedido.recebidoAte !== "") {
      partes.push(`recebidoAte=${pedido.recebidoAte}`);
    }
    if (pedido.ordenarPor !== undefined) {
      partes.push(`ordenarPor=${pedido.ordenarPor}`);
      // O sentido só acompanha a coluna. Sozinho ele não significa nada, e
      // mandá-lo sem coluna faria o servidor devolver a ordem padrão com o
      // sentido do cliente — uma terceira ordem que ninguém pediu.
      if (pedido.sentido !== undefined) {
        partes.push(`sentido=${pedido.sentido}`);
      }
    }
    return requisitar<Lote[]>(`/producao/lotes?${partes.join("&")}`);
  },

  lote: (empresa: string, id: string) =>
    requisitar<Lote>(`/producao/lotes/${id}?empresa=${empresa}`),

  /**
   * O lote pelo **código impresso** — o que a tela aberta pelo QR usa.
   *
   * **Sem empresa, e é a única consulta de lote assim.** Quem escaneia um
   * tambor não tem CNPJ escolhido; o código composto é único no grupo e já diz
   * de quem é o material. Pedir a empresa aqui seria exigir a resposta junto
   * com a pergunta.
   *
   * Exige sessão como todas as outras: o lote carrega dado comercial —
   * fornecedor, quantidade, nota fiscal —, e o código impresso é público de
   * fato (quem tem o tambor tem o número). Fosse pública, o código do lote
   * viraria a credencial de acesso ao dado de compra.
   */
  lotePorCodigo: (codigo: string) =>
    requisitar<Lote>(`/producao/lotes/por-codigo/${encodeURIComponent(codigo)}`),

  /**
   * Os recipientes do lote — **um por etiqueta**, cada um com o token do QR
   * dele.
   *
   * Chamada separada, e não um campo de `Lote`: a lista de lotes carrega
   * dezenas de linhas e não usa volume nenhum. Embutir vinte tokens em cada
   * linha faria a tela de conferência pagar por um dado que só a tela de
   * impressão lê.
   */
  volumesDoLote: (empresa: string, loteId: string) =>
    requisitar<VolumeDaEtiqueta[]>(`/producao/lotes/${loteId}/volumes?empresa=${empresa}`),

  /**
   * O volume pelo **token impresso** — o que o QR daquela bombona abre.
   *
   * **Sem empresa e sem código do lote.** Quem escaneou está no corredor com o
   * recipiente na mão: não escolheu CNPJ, não digitou código, e muitas vezes
   * não sabe de qual lote é o tambor — que é parte do que veio perguntar. O
   * token é único no grupo e carrega tudo isso dentro de si.
   *
   * Exige sessão, como `lotePorCodigo`: o token é opaco, mas obscuridade de
   * endereço não é controle de acesso — o adesivo está colado num tambor que
   * atravessa transportadora.
   */
  volumePorToken: (token: string) =>
    requisitar<VolumeNaTela>(`/producao/volumes/${encodeURIComponent(token)}`),

  receber: (pedido: PedidoDeRecebimento) =>
    requisitar<{ id: string }>("/producao/lotes", { metodo: "POST", corpo: pedido }),

  /**
   * Registra as etiquetas que vão sair — **uma emissão por adesivo**.
   *
   * A impressão acontece no navegador; o que o servidor guarda é o **rastro** e
   * a **cópia do que foi para o papel**.
   *
   * ⚠️ **Deixou de receber uma contagem de vias.** `{ vias: 3 }` gravava
   * `"3 via(s) da etiqueta do lote X"` — que não diz de qual tambor, e faz
   * reimprimir o 7 e o 12 produzirem a mesma linha. Agora vão os **números dos
   * volumes**, e cada um vira uma emissão com a via dele.
   *
   * `motivo` é obrigatório quando algum dos volumes pedidos já teve etiqueta.
   * Reimprimir é normal — etiqueta rasga, molha e cai atrás do tambor —; o que
   * não pode é reimprimir sem rastro. A recusa é do servidor **e** do banco.
   *
   * 🔴 **`empresa` passou a ser o primeiro argumento na #27.** Ela não ia: o
   * servidor lia a empresa de dentro do próprio lote, o que equivale a não
   * conferir nada — qualquer sessão emitia etiqueta de qualquer lote cujo
   * identificador conhecesse, inclusive de outra pessoa jurídica do grupo.
   * Etiqueta emitida pela empresa errada é **documento inválido colado num
   * tambor** (ADR-0005 §1). Quem confere é o servidor; o que muda aqui é que
   * ele passa a ter contra o que conferir.
   */
  registrarImpressao: (
    empresa: string,
    loteId: string,
    volumes: readonly number[],
    motivo?: string,
  ) =>
    requisitar<EtiquetaEmitida[]>(
      `/producao/lotes/${loteId}/etiqueta/impressa?empresa=${empresa}`,
      {
        metodo: "POST",
        corpo: { volumes, motivo: motivo !== undefined && motivo !== "" ? motivo : null },
      },
    ),

  materiasPrimas: (empresa: string) =>
    requisitar<MateriaPrima[]>(`/producao/materias-primas?empresa=${empresa}`),

  /**
   * O tipo e a espécie entram **aqui**, no cadastro, e não no recebimento.
   *
   * São característica do material, como a unidade de medida. Digitados a cada
   * entrada, o mesmo insumo sairia como `MP-COP-…` numa nota e `EX-COP-…` na
   * seguinte — e o código do lote deixaria de dizer o que diz.
   */
  cadastrarMateriaPrima: (
    empresa: string,
    codigo: string,
    nome: string,
    tipo: TipoDeMaterial,
    especie: string,
    unidade: UnidadeDeMedida,
  ) =>
    requisitar<MateriaPrima>("/producao/materias-primas", {
      metodo: "POST",
      corpo: { empresa, codigo, nome, tipo, especie, unidade },
    }),

  /**
   * Os fornecedores que a Produção pode escolher — **só os qualificados**.
   *
   * Não existe `cadastrarFornecedor` aqui, e a ausência é o controle: quem
   * recebe material não cria o fornecedor na hora. Cadastrar e qualificar são
   * atos de Assuntos Regulatórios (`fornecedores.cadastrar`).
   */
  fornecedores: (empresa: string) =>
    requisitar<Fornecedor[]>(`/producao/fornecedores?empresa=${empresa}`),
};
